import { useAccountContext } from 'utils/account/AccountContext';
import { DAMAGES2_DEVENTING2_RT } from 'utils/release-toggles';
import { message } from 'antd'; // Intentionally left on antd v4 for style consistency
import { ApolloError } from '@apollo/client';
import {
  AtlasGqlMergeHorizonDamagesMutation,
  useMergeHorizonDamagesMutation,
} from 'types/atlas-graphql';
import { formatDamageId } from 'horizon/components/Damages/utils';
import { ConfirmationModal } from 'components/ConfirmationModal';

export type MergeConfirmationState = {
  isOpen: boolean;
  observationCount?: number;
  targetDamageId?: string;
  sourceDamageIds?: string[];
};

type MergeDamagesModalProps = {
  mergeConfirmationState: MergeConfirmationState;
  onClose: () => void;
  onSuccess?: (data: AtlasGqlMergeHorizonDamagesMutation) => void;
  onError?: (error: ApolloError) => void;
};

export const MergeDamagesModal: React.FunctionComponent<MergeDamagesModalProps> = ({
  mergeConfirmationState,
  onClose,
  onSuccess,
  onError,
}) => {
  const { isOpen, observationCount, targetDamageId, sourceDamageIds = [] } = mergeConfirmationState;

  const { hasReleaseToggle } = useAccountContext();
  const hasDamages2Deventing2Toggle = hasReleaseToggle(DAMAGES2_DEVENTING2_RT);

  // merge damage mutation
  const [mergeDamages, { loading: mergeLoading }] = useMergeHorizonDamagesMutation({
    onCompleted: data => {
      targetDamageId &&
        message.success(
          <>
            Damage successfully linked to Damage{' '}
            <a href={`/damages2/${targetDamageId}`} target="_blank" rel="noreferrer">
              {formatDamageId({ id: targetDamageId })}
            </a>
          </>
        );
      onSuccess && onSuccess(data);

      onClose();
    },
    onError: error => {
      message.error('There was an error linking the damages. Please try again.');
      onError && onError(error);

      onClose();
    },
  });

  const damageIdsToLink = [targetDamageId, ...sourceDamageIds];

  return (
    <ConfirmationModal
      open={isOpen}
      loading={mergeLoading}
      title="Confirm Damage Link"
      okText="Link"
      onOk={() => {
        if (!!targetDamageId && !!sourceDamageIds) {
          mergeDamages({
            variables: {
              input: {
                targetDamageId,
                sourceDamageIds,
              },
            },
          });
        }
      }}
      cancelText="Cancel"
      onCancel={onClose}
      message={
        `This action will link ${damageIdsToLink.length} Damages, with ${observationCount} Observations` +
        ` and all associated ${hasDamages2Deventing2Toggle ? 'Tasks and Comments' : 'Tasks'}, together` +
        ` under Damage ID ${formatDamageId({ id: targetDamageId })}.` +
        ` This action cannot be undone.`
      }
    />
  );
};
